import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserProfile, User, Address, FormError } from '../types';
import { HttpClient } from '@angular/common/http';
import { ServiceRoot } from './service.root';
import { environment } from '../../environments/environment';

export interface MagicLinkResponse {
  token: string;
  profile: UserProfile;
}

export interface OrderStatus {
  status: string;
  design_uuid: string;
  order_uuid: string;
  addresses_available: boolean;
  order_desc: string;
  design_text: string;
  total_postcards: number;
  total_price_usd: number;
  created_at: string;
  total_price: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  service_root = inject(ServiceRoot);
  base_url = this.service_root.base_url() + '/api/users/';
  currentUser: User | null = null;
  currentJWT: string | null = null;
  currentAddress: Address | null = null;

  constructor(private http: HttpClient) {}

  sendMagicLink(email: string): Observable<string> {
    const data = JSON.stringify({
      email: email,
      host: environment.APP_URI,
    });
    return this.http.post(this.base_url + 'magic_link/send', data, {
      headers: { 'Content-Type': 'application/json' },
    }) as Observable<string>;
  }

  usernameAvailable(username: string, email: string): Observable<boolean> {
    return this.http
      .post<{ available: boolean }>(
        this.base_url + 'username/available',
        { username: username, email: email },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .pipe(
        map((response) => response.available),
        catchError(() => of(false))
      );
  }

  magicLogin(b64id: string, token: string): Observable<MagicLinkResponse> {
    return this.http
      .get<MagicLinkResponse>(
        this.base_url + 'magic_link/' + b64id + '/' + token,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .pipe(
        map((response: MagicLinkResponse) => {
          this.currentJWT = response.token;
          this.currentUser = response.profile.user;
          this.currentAddress = response.profile.address;
          return response;
        }),
        catchError((error) => {
          console.error('Error during magicLogin:', error);
          return of(null as any);
        })
      ) as Observable<MagicLinkResponse>;
  }

  logout() {
    this.currentUser = null;
    this.currentJWT = null;
    this.currentAddress = null;
  }

  getOrders(): Observable<OrderStatus[]> {
    return this.http.get<OrderStatus[]>(this.base_url + 'user_orders/', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.currentJWT,
      },
    }) as Observable<OrderStatus[]>;
  }

  updateUser(user: User): Observable<User|FormError> {
    const data = JSON.stringify(user);
    return this.http
      .post<User>(this.base_url + 'users/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + this.currentJWT,
        },
      })
      .pipe(
        map((user: User) => {
          this.currentUser = user;
          return user;
        }),
        catchError((error) => {
          const formErrors = new FormError();
          for (const key in error.error) {
            formErrors.fieldErrors.set(key, error.error[key]);
          }
          return of(formErrors as User|FormError);
        })
      );
  }
}
